/**
 * Servicio para el cambio de contraseña en 3 etapas para Club Mia
 * Maneja la verificación de identificación, envío de código por WhatsApp y cambio de contraseña
 */

import ApiService from './api';

class ChangePasswordService {
  /**
   * ETAPA 1: Solicitar código de verificación
   * @param {string} identificationNumber - Número de identificación del usuario
   * @returns {Promise<Object>} Respuesta con información del usuario y estado del envío
   */
  async requestVerificationCode(identificationNumber) {
    try {
      const response = await ApiService.post('/api/V1/club-mia/password-reset/request', {
        identification_number: identificationNumber.trim(),
        sian_domain: process.env.REACT_APP_SIAN_DOMAIN
      });

      if (response.success) {
        return {
          success: true,
          data: response.data,
          message: response.message || 'Código enviado por WhatsApp'
        };
      } else {
        throw new Error(response.error || 'Error en la verificación');
      }
    } catch (error) {
      console.error('Error requesting verification code:', error);

      // Simulación para desarrollo (remover en producción)
      if (process.env.NODE_ENV === 'development') {
        return this.simulateRequestCode(identificationNumber);
      }

      return {
        success: false,
        message: error.message || 'Error de conexión. Inténtalo de nuevo.'
      };
    }
  }

  /**
   * ETAPA 2: Verificar código de confirmación recibido por WhatsApp
   * @param {string} identificationNumber - Número de identificación
   * @param {string} verificationCode - Código recibido por WhatsApp (máx 10 caracteres)
   * @returns {Promise<Object>} Respuesta de verificación
   */
  async verifyCode(identificationNumber, verificationCode) {
    try {
      const response = await ApiService.post('/api/V1/club-mia/password-reset/verify', {
        identification_number: identificationNumber.trim(),
        verification_code: verificationCode.trim(),
        sian_domain: process.env.REACT_APP_SIAN_DOMAIN
      });

      if (response.success) {
        return {
          success: true,
          data: response.data,
          message: response.message || 'Verification code validated successfully'
        };
      } else {
        throw new Error(response.error || 'Código incorrecto');
      }
    } catch (error) {
      console.error('Error verifying code:', error);

      // Simulación para desarrollo (remover en producción)
      if (process.env.NODE_ENV === 'development') {
        return this.simulateVerifyCode(verificationCode);
      }

      return {
        success: false,
        message: error.message || 'Error de conexión. Inténtalo de nuevo.'
      };
    }
  }

  /**
   * ETAPA 3: Establecer nueva contraseña
   * @param {string} identificationNumber - Número de identificación
   * @param {string} verificationCode - Código de verificación
   * @param {string} newPassword - Nueva contraseña (mín 6 caracteres)
   * @param {string} confirmPassword - Confirmación de contraseña
   * @returns {Promise<Object>} Respuesta del cambio de contraseña
   */
  async changePassword(identificationNumber, verificationCode, newPassword, confirmPassword) {
    try {
      const response = await ApiService.post('/api/V1/club-mia/password-reset/confirm', {
        identification_number: identificationNumber.trim(),
        verification_code: verificationCode.trim(),
        new_password: newPassword,
        new_password_confirmation: confirmPassword,
        sian_domain: process.env.REACT_APP_SIAN_DOMAIN
      });

      if (response.success) {
        return {
          success: true,
          data: response.data,
          message: response.message || 'Password reset successfully'
        };
      } else {
        throw new Error(response.error || 'Error al cambiar la contraseña');
      }
    } catch (error) {
      console.error('Error changing password:', error);

      // Simulación para desarrollo (remover en producción)
      if (process.env.NODE_ENV === 'development') {
        return this.simulateChangePassword();
      }

      return {
        success: false,
        message: error.message || 'Error de conexión. Inténtalo de nuevo.'
      };
    }
  }

  /**
   * Reenviar código de verificación por WhatsApp
   * Reutiliza el endpoint de la ETAPA 1
   * @param {string} identificationNumber - Número de identificación
   * @returns {Promise<Object>} Respuesta del reenvío
   */
  async resendCode(identificationNumber) {
    try {
      // Reutilizar el mismo endpoint de solicitud de código
      const response = await ApiService.post('/api/V1/club-mia/password-reset/request', {
        identification_number: identificationNumber.trim(),
        sian_domain: process.env.REACT_APP_SIAN_DOMAIN
      });

      if (response.success) {
        return {
          success: true,
          message: response.message || 'Código reenviado por WhatsApp'
        };
      } else {
        throw new Error(response.error || 'Error al reenviar código');
      }
    } catch (error) {
      console.error('Error resending code:', error);

      // Simulación para desarrollo (remover en producción)
      if (process.env.NODE_ENV === 'development') {
        return {
          success: true,
          message: 'Código reenviado por WhatsApp (simulación)'
        };
      }

      return {
        success: false,
        message: error.message || 'Error de conexión. Inténtalo de nuevo.'
      };
    }
  }

  // ========== MÉTODOS DE SIMULACIÓN PARA DESARROLLO ==========
  // Estos métodos deben ser removidos en producción

  simulateRequestCode(identificationNumber) {
    // Simular algunos números válidos para testing
    const validIds = ['12345678', '87654321', '11111111'];

    if (validIds.includes(identificationNumber)) {
      return {
        success: true,
        data: {
          user_id: 1,
          name: 'Usuario de Prueba',
          masked_phone: '***-***-1234',
          identification_number: identificationNumber
        },
        message: 'Verification code sent successfully via WhatsApp'
      };
    } else {
      return {
        success: false,
        message: 'Número de identificación no encontrado'
      };
    }
  }

  simulateVerifyCode(verificationCode) {
    // Código de prueba: 123456
    if (verificationCode === '123456') {
      return {
        success: true,
        data: {
          verified: true,
          timestamp: new Date().toISOString()
        },
        message: 'Verification code validated successfully'
      };
    } else {
      return {
        success: false,
        message: 'Código incorrecto. Usa 123456 para pruebas.'
      };
    }
  }

  simulateChangePassword() {
    return {
      success: true,
      data: {
        user_id: 1,
        updated_at: new Date().toISOString()
      },
      message: 'Password reset successfully'
    };
  }
}

export default new ChangePasswordService();
